import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, MessageCircle, Phone, CheckCircle2, ShieldCheck, Sparkles,
  Search, BarChart3, Bot, GitBranch, Zap, Megaphone, ChevronDown,
  TrendingUp, Briefcase, GraduationCap, Building2, UserCheck, Globe,
  Award, Users, Clock, Target, Mail, Headphones, Star
} from 'lucide-react';
import PageMeta from '../components/PageMeta';
import SiteHeader from '../components/SiteHeader';
import SiteFooter from '../components/SiteFooter';
import MobileCTABar from '../components/MobileCTABar';

/* ==========================================================================
   BRAND TOKENS — per spec
   purple #4F46E5 · orange #FF6B35 · blue #2563EB · green #16A34A · white
   ========================================================================== */
const BRAND = {
  PHONE_DISPLAY: '+91 98919 90666',
  PHONE_TEL: '+919891990666',
  WA_URL: 'https://wa.me/919891990666?text=Hi,%20I%27m%20interested%20in%20the%20Performance%20Marketing%20Course.%20Please%20share%20details.',
};

const HEADING_FONT = "'Poppins', system-ui, sans-serif";
const BODY_FONT    = "'Inter', system-ui, sans-serif";

const img = (id, w = 800) =>
  `https://images.unsplash.com/${id}?w=${w}&q=80&auto=format&fit=crop`;

const HERO_DASH_IMG = img('photo-1460925895917-afdab827c52f', 1200);

/* ==========================================================================
   PAGE
   ========================================================================== */
const PerformanceMarketingPage = () => {
  const [openFAQ, setOpenFAQ] = useState(0);
  const [form, setForm] = useState({
    name: '', phone: '', email: '', course: 'Performance Marketing Course', city: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formErr, setFormErr] = useState('');

  // Inject font on root so the whole page uses Inter/Poppins
  useEffect(() => {
    const prev = document.body.style.fontFamily;
    document.body.style.fontFamily = BODY_FONT;
    return () => { document.body.style.fontFamily = prev; };
  }, []);

  const submitDemo = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email) {
      setFormErr('Please fill in name, phone and email.'); return;
    }
    setFormErr(''); setSubmitting(true);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${API_URL}/api/leads`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name, email: form.email, phone: form.phone,
          course: form.course,
          message: `City: ${form.city || 'N/A'} · Interested in ${form.course}`,
          source: 'performance-marketing-inline-form',
        }),
      });
      if (!res.ok) throw new Error('Submit failed');
      if (window.gtag) window.gtag('event', 'lead_submit', { source: 'pm-inline-form' });
      setSubmitted(true);
    } catch (err) {
      setFormErr('Something went wrong. Please WhatsApp us or try again.');
    } finally { setSubmitting(false); }
  };

  /* -------------------- DATA -------------------- */
  const tools = [
    { icon: <Search className="w-7 h-7" />,     bg: 'bg-blue-50',    fg: 'text-blue-600',    t: 'Google Ads',                 d: 'Search · Display · YouTube · Performance Max — campaigns that scale profitably.' },
    { icon: <Megaphone className="w-7 h-7" />,   bg: 'bg-indigo-50',  fg: 'text-indigo-600',  t: 'Meta Ads',                   d: 'Facebook & Instagram audience targeting, creatives, retargeting and Lead Gen Ads.' },
    { icon: <Bot className="w-7 h-7" />,         bg: 'bg-violet-50',  fg: 'text-violet-600',  t: 'AI Marketing Stack',         d: 'ChatGPT, Claude, Gemini — AI copy, creatives, automation, end-to-end marketing.' },
    { icon: <BarChart3 className="w-7 h-7" />,   bg: 'bg-orange-50',  fg: 'text-orange-600',  t: 'Analytics & GA4',            d: 'Google Tag Manager, GA4, Looker Studio — track every click, build attribution dashboards.' },
    { icon: <GitBranch className="w-7 h-7" />,   bg: 'bg-green-50',   fg: 'text-green-600',   t: 'Funnels & Automation',       d: 'End-to-end funnels, email workflows, lead nurturing automation that converts.' },
    { icon: <Zap className="w-7 h-7" />,         bg: 'bg-blue-50',    fg: 'text-blue-600',    t: 'CRM, Zapier & Make',         d: 'Zoho CRM, Zapier, Make.com — connect every tool, automate every workflow.' },
  ];

  const modules = [
    { n: '01', t: 'Performance Marketing Foundation', h: 20, d: 'Marketing Fundamentals · Digital Ecosystem · Customer Journey · Funnels & Strategy' },
    { n: '02', t: 'Google Ads Mastery',               h: 50, d: 'Search · Display · YouTube · Performance Max · Keyword Research · Bidding Strategies' },
    { n: '03', t: 'Meta Ads Mastery',                 h: 40, d: 'Facebook · Instagram · Audience Targeting · Retargeting · Lead Gen Ads · Creatives' },
    { n: '04', t: 'AI Marketing Stack',               h: 30, d: 'ChatGPT · Gemini · Claude · AI Content · AI Ad Copy · Marketing Automation' },
    { n: '05', t: 'Analytics & Tracking',             h: 20, d: 'GA4 · Google Tag Manager · Conversion Tracking · Looker Studio Dashboards' },
    { n: '06', t: 'Automation & CRM',                 h: 20, d: 'Zapier · Make · Zoho CRM · Email Workflows · Lead Nurturing' },
    { n: '07', t: 'Live Projects & Portfolio',        h: 20, d: 'Real Campaign Execution · Portfolio · Case Studies · Client Presentation' },
  ];

  const careers = [
    { t: 'Performance Marketing Specialist', salary: '₹4–8 LPA',          for_: 'Agencies · Startups · Corporates',     popular: false },
    { t: 'Google Ads Specialist',            salary: '₹5–12 LPA',         for_: 'E-commerce · SaaS · Agencies',         popular: false },
    { t: 'Meta Ads Specialist',              salary: '₹4–10 LPA',         for_: 'D2C Brands · Agencies · Startups',     popular: false },
    { t: 'Marketing Automation Executive',   salary: '₹5–12 LPA',         for_: 'Tech Companies · SaaS · Enterprises',  popular: false },
    { t: 'Digital Marketing Manager',        salary: '₹8–20 LPA',         for_: 'Corporates · Agencies · Enterprises',  popular: true  },
    { t: 'Freelancer / Consultant',          salary: 'Unlimited Income',  for_: 'Local Businesses · Global Clients',    popular: false },
  ];

  const perfectFor = [
    { icon: <GraduationCap className="w-6 h-6" />, t: 'Students' },
    { icon: <UserCheck     className="w-6 h-6" />, t: 'Working Professionals' },
    { icon: <Building2     className="w-6 h-6" />, t: 'Entrepreneurs' },
    { icon: <TrendingUp    className="w-6 h-6" />, t: 'Career Switchers' },
    { icon: <Briefcase     className="w-6 h-6" />, t: 'Freelancers' },
    { icon: <Award         className="w-6 h-6" />, t: 'Business Owners' },
  ];

  const why = [
    { icon: <Globe className="w-6 h-6" />,    t: 'Global Client Exposure',         d: 'Work with global brands and markets during the course.' },
    { icon: <Target className="w-6 h-6" />,   t: 'Million Dollar Project Experience', d: 'Learn from high-value real-world projects, not theory.' },
    { icon: <Briefcase className="w-6 h-6" />,t: 'Practical Industry Learning',    d: 'Job-oriented skills for real industry needs.' },
    { icon: <Bot className="w-6 h-6" />,      t: 'AI Integrated Curriculum',       d: 'Future-ready learning with the latest AI tools.' },
    { icon: <TrendingUp className="w-6 h-6" />, t: 'Career Focused Programs',      d: 'Designed to build careers, not just certificates.' },
    { icon: <Users className="w-6 h-6" />,    t: 'Mentorship & Growth',            d: 'Guidance, support and continuous growth from industry pros.' },
  ];

  const faqs = [
    { q: 'What is the duration of this course?',
      a: '5 months · 200 learning hours · Live online with industry mentors. Includes Saturday/weekend cohorts and recorded sessions for review.' },
    { q: 'Do I need prior experience?',
      a: 'No prior marketing experience required. The course starts from fundamentals and ramps up to advanced campaigns — 60% of our students come from non-marketing backgrounds.' },
    { q: 'What certificate do I get?',
      a: 'An industry-aligned certificate from aiSchool for All powered by Infinite Web Marketing. Recognised by 200+ hiring partners across India.' },
    { q: 'What salary can I expect after this course?',
      a: 'Our students typically get placed in the ₹4–20 LPA range depending on the role and city — Performance Marketing Specialist starts at ₹4–8 LPA; Digital Marketing Manager goes up to ₹20 LPA.' },
    { q: 'Is placement assistance provided?',
      a: 'Yes — full career support: resume prep, mock interviews, portfolio review, and direct interviews with 200+ hiring partners.' },
  ];

  /* -------------------- RENDER -------------------- */
  return (
    <div className="min-h-screen bg-white text-slate-900" style={{ fontFamily: BODY_FONT }}>
      <PageMeta
        title="Performance Marketing Course in Delhi NCR | Job-Assured | ₹4–20 LPA | aiSchool for All"
        description="Master Google Ads, Meta Ads & AI Marketing in 200 hours. 5-month live program. Get hired at ₹4–20 LPA or become a freelance consultant. Book free demo. Call +91 98919 90666"
        path="/performance-marketing"
      />

      {/* === URGENCY ANNOUNCEMENT BAR === */}
      <div className="bg-[#FF6B35] text-white text-center py-2.5 px-4 text-sm font-semibold">
        <span className="inline-flex items-center gap-2 flex-wrap justify-center">
          🔥 New Batch Starting Soon · Limited Seats ·
          <a href={`tel:${BRAND.PHONE_TEL}`} className="underline font-bold">WhatsApp {BRAND.PHONE_DISPLAY}</a>
        </span>
      </div>

      <SiteHeader ctaLabel="Book Free Demo" ctaTo="/book-demo" />

      {/* ===== SECTION 1: HERO ===== */}
      <section className="relative bg-white overflow-hidden">
        {/* Soft purple gradient edges */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#F8F7FF] via-white to-[#FFF5F0] pointer-events-none" />
        <div className="absolute -top-32 -right-32 w-[520px] h-[520px] bg-[#4F46E5]/10 rounded-full blur-3xl" />

        <div className="container mx-auto max-w-7xl px-6 pt-16 pb-20 relative">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            {/* LEFT */}
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 bg-[#4F46E5]/10 text-[#4F46E5] rounded-full px-4 py-1.5 mb-5">
                <Sparkles className="w-3.5 h-3.5" />
                <span className="text-[11px] font-black uppercase tracking-widest">Admissions Open · Limited Seats</span>
              </div>

              <h1
                style={{ fontFamily: HEADING_FONT }}
                className="text-4xl md:text-5xl lg:text-[58px] leading-[1.1] font-black text-slate-900 mb-4"
              >
                Master{' '}
                <span className="relative inline-block text-[#4F46E5]">
                  Performance Marketing
                  <span className="absolute left-0 right-0 -bottom-1 h-1.5 bg-[#FF6B35] rounded" aria-hidden />
                </span>
                {' '}with AI
              </h1>

              <p className="text-lg md:text-xl text-slate-700 mb-6 max-w-xl leading-relaxed">
                Get Hired at <strong className="text-[#2563EB]">₹4–20 LPA</strong> or Become a Freelance Consultant.
                <span className="block mt-1">Two Career Paths. One Powerful Skillset.</span>
              </p>

              {/* Stat pills */}
              <div className="flex flex-wrap gap-2 mb-7">
                {['200 Learning Hours', '5 Months', 'Live Online', 'AI Tools Included', 'Industry Certificate'].map((s) => (
                  <span key={s} className="text-xs md:text-sm font-bold text-[#4F46E5] bg-white border border-[#4F46E5]/30 rounded-full px-3.5 py-1.5">
                    {s}
                  </span>
                ))}
              </div>

              {/* CTAs */}
              <div className="flex flex-col sm:flex-row gap-3 max-w-xl">
                <Link
                  to="/book-demo"
                  data-testid="pm-hero-book-demo"
                  className="flex-1 inline-flex items-center justify-center gap-2 bg-[#4F46E5] hover:bg-[#3F37C9] text-white px-7 py-4 rounded-xl font-black text-base shadow-lg shadow-[#4F46E5]/30 transition-all hover:scale-[1.02]"
                  style={{ fontFamily: HEADING_FONT }}
                >
                  Book Free Demo <ArrowRight className="w-5 h-5" />
                </Link>
                <a
                  href={BRAND.WA_URL}
                  target="_blank" rel="noopener noreferrer"
                  data-testid="pm-hero-whatsapp"
                  className="flex-1 inline-flex items-center justify-center gap-2 bg-[#16A34A] hover:bg-[#15803D] text-white px-7 py-4 rounded-xl font-black text-base shadow-lg transition-all"
                  style={{ fontFamily: HEADING_FONT }}
                >
                  <MessageCircle className="w-5 h-5" /> WhatsApp Us
                </a>
              </div>

              <p className="mt-5 text-xs md:text-sm text-slate-600 inline-flex items-center gap-3 flex-wrap">
                <Star className="w-4 h-4 text-yellow-500" />
                Trusted by 500+ Students · Powered by Infinite Web Marketing · 15+ Years Experience
              </p>
            </div>

            {/* RIGHT — Campaign Dashboard Mock */}
            <div className="lg:col-span-5">
              <div className="relative">
                <img
                  src={HERO_DASH_IMG}
                  alt="Performance marketing analytics dashboard on laptop"
                  width="640" height="420"
                  loading="eager" fetchPriority="high" decoding="async"
                  className="w-full rounded-2xl shadow-2xl object-cover h-[280px] md:h-[360px]"
                />
                {/* Floating metric cards over the image */}
                <div className="absolute -left-3 -bottom-5 bg-white rounded-2xl shadow-2xl border border-slate-100 px-5 py-4 max-w-[220px]">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Live Campaign</p>
                  <div className="grid grid-cols-3 gap-3 mt-2">
                    <div><p className="text-[10px] text-slate-500">Clicks</p><p className="text-sm font-black text-[#4F46E5]">25.6K</p></div>
                    <div><p className="text-[10px] text-slate-500">Conv.</p><p className="text-sm font-black text-[#16A34A]">1.35K</p></div>
                    <div><p className="text-[10px] text-slate-500">Cost</p><p className="text-sm font-black text-[#FF6B35]">₹67.2K</p></div>
                  </div>
                </div>
                <div className="absolute -right-3 -top-4 bg-[#4F46E5] text-white rounded-xl shadow-xl px-4 py-3">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-white/70">ROAS</p>
                  <p className="text-2xl font-black">8.4x</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== SOCIAL PROOF TICKER ===== */}
      <div className="bg-[#F5F3FF] border-y border-[#4F46E5]/10 py-3 overflow-hidden">
        <div className="container mx-auto max-w-7xl px-6 text-sm text-[#4F46E5] font-semibold text-center">
          🎓 Rahul from Noida just enrolled · ⭐ Priya completed and got placed at ₹6 LPA · 🔥 3 students enrolled today
        </div>
      </div>

      {/* ===== SECTION 2: STATS BAR ===== */}
      <section className="bg-[#4F46E5] py-10 px-6">
        <div className="container mx-auto max-w-6xl grid grid-cols-2 md:grid-cols-4 divide-y md:divide-y-0 md:divide-x divide-white/20 text-center">
          {[
            { n: '200+', t: 'Learning Hours' },
            { n: '5',    t: 'Month Program' },
            { n: '₹4–20 LPA', t: 'Salary Range' },
            { n: '15+',  t: 'Years Experience' },
          ].map((s, i) => (
            <div key={i} className="py-4 md:py-0 md:px-4">
              <p style={{ fontFamily: HEADING_FONT }} className="text-3xl md:text-4xl font-black text-[#FF6B35]">{s.n}</p>
              <p className="text-white/90 text-sm font-semibold mt-1">{s.t}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ===== SECTION 3: WHAT YOU LEARN ===== */}
      <section className="py-20 px-6 bg-white">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#4F46E5] mb-2">Skills That Pay</p>
            <h2 style={{ fontFamily: HEADING_FONT }} className="text-3xl md:text-5xl font-black text-slate-900 leading-tight">
              Master the Tools That <span className="text-[#FF6B35]">Employers Pay Premium</span> Salaries For
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {tools.map((m, i) => (
              <div key={i} className="bg-white border border-slate-200 rounded-2xl p-7 hover:shadow-xl hover:-translate-y-1 hover:border-[#4F46E5]/30 transition-all">
                <div className={`w-14 h-14 rounded-2xl ${m.bg} ${m.fg} flex items-center justify-center mb-4`}>{m.icon}</div>
                <h3 style={{ fontFamily: HEADING_FONT }} className="text-xl font-black text-slate-900 mb-2">{m.t}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{m.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== SECTION 4: TWO CAREER PATHS ===== */}
      <section className="py-20 px-6 bg-[#F5F3FF]">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#4F46E5] mb-2">Choose Your Path</p>
            <h2 style={{ fontFamily: HEADING_FONT }} className="text-3xl md:text-5xl font-black text-slate-900 leading-tight">
              Same Skills. <span className="text-[#FF6B35]">Two Powerful Paths.</span> Learn Once. Earn for Years.
            </h2>
          </div>

          <div className="relative grid md:grid-cols-2 gap-6">
            {/* VS divider */}
            <div className="hidden md:flex absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10 w-16 h-16 rounded-full bg-white shadow-xl border-2 border-[#4F46E5] items-center justify-center">
              <span style={{ fontFamily: HEADING_FONT }} className="text-xl font-black text-[#4F46E5]">VS</span>
            </div>

            {/* GET HIRED */}
            <div className="bg-white border-2 border-[#4F46E5] rounded-3xl p-8 shadow-lg">
              <div className="inline-flex items-center gap-2 bg-[#4F46E5] text-white rounded-full px-4 py-1.5 mb-4">
                <Briefcase className="w-4 h-4" />
                <span className="text-xs font-black uppercase tracking-widest">Path 1 · Get Hired</span>
              </div>
              <h3 style={{ fontFamily: HEADING_FONT }} className="text-2xl md:text-3xl font-black text-slate-900 mb-4">
                Land a Premium Marketing Job
              </h3>
              <ul className="space-y-2.5 mb-6 text-slate-700">
                {['Performance Marketing Specialist', 'Google Ads Expert', 'Meta Ads Specialist', 'Digital Marketing Manager'].map((r) => (
                  <li key={r} className="flex items-start gap-2"><CheckCircle2 className="w-5 h-5 text-[#16A34A] flex-shrink-0 mt-0.5" /><span>{r}</span></li>
                ))}
              </ul>
              <div className="bg-[#FFF5F0] border border-[#FF6B35]/30 rounded-xl p-4">
                <p className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-1">Salary</p>
                <p style={{ fontFamily: HEADING_FONT }} className="text-3xl font-black text-[#FF6B35]">₹4–20 LPA</p>
                <p className="text-xs text-slate-600 mt-1">Hiring: Agencies, Startups, Corporates</p>
              </div>
            </div>

            {/* FREELANCE */}
            <div className="bg-white border-2 border-[#FF6B35] rounded-3xl p-8 shadow-lg">
              <div className="inline-flex items-center gap-2 bg-[#FF6B35] text-white rounded-full px-4 py-1.5 mb-4">
                <Globe className="w-4 h-4" />
                <span className="text-xs font-black uppercase tracking-widest">Path 2 · Freelance Consultant</span>
              </div>
              <h3 style={{ fontFamily: HEADING_FONT }} className="text-2xl md:text-3xl font-black text-slate-900 mb-4">
                Run Your Own Marketing Business
              </h3>
              <ul className="space-y-2.5 mb-6 text-slate-700">
                {['Google Ads Services', 'Meta Ads Management', 'Lead Generation Services', 'AI Marketing Services'].map((r) => (
                  <li key={r} className="flex items-start gap-2"><CheckCircle2 className="w-5 h-5 text-[#16A34A] flex-shrink-0 mt-0.5" /><span>{r}</span></li>
                ))}
              </ul>
              <div className="bg-[#F5F3FF] border border-[#4F46E5]/30 rounded-xl p-4">
                <p className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-1">Income Potential</p>
                <p style={{ fontFamily: HEADING_FONT }} className="text-3xl font-black text-[#4F46E5]">Unlimited</p>
                <p className="text-xs text-slate-600 mt-1">Work with: Local Businesses · Global Clients · Startups</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== SECTION 5: CURRICULUM ===== */}
      <section id="curriculum" className="py-20 px-6 bg-white">
        <div className="container mx-auto max-w-5xl">
          <div className="text-center mb-12">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#4F46E5] mb-2">The Curriculum</p>
            <h2 style={{ fontFamily: HEADING_FONT }} className="text-3xl md:text-5xl font-black text-slate-900 leading-tight">
              200 Hours · 7 Modules · Real Campaigns · Real Results
            </h2>
          </div>

          <div className="space-y-4">
            {modules.map((m) => (
              <div key={m.n} className="bg-white border border-slate-200 rounded-2xl p-6 hover:border-[#4F46E5] hover:shadow-lg transition-all flex flex-col md:flex-row gap-4">
                <div className="flex-shrink-0 w-20 text-center">
                  <p style={{ fontFamily: HEADING_FONT }} className="text-4xl md:text-5xl font-black text-[#4F46E5]/30">{m.n}</p>
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-3 flex-wrap mb-2">
                    <h3 style={{ fontFamily: HEADING_FONT }} className="text-xl font-black text-slate-900">{m.t}</h3>
                    <span className="text-xs font-black uppercase tracking-widest text-white bg-[#FF6B35] rounded-full px-3 py-1">{m.h} HRS</span>
                  </div>
                  <p className="text-sm text-slate-600 leading-relaxed">{m.d}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 bg-[#F5F3FF] rounded-2xl p-5 flex items-center justify-between gap-4">
            <p style={{ fontFamily: HEADING_FONT }} className="font-black text-[#4F46E5]">Total Program</p>
            <p style={{ fontFamily: HEADING_FONT }} className="text-2xl font-black text-[#FF6B35]">200 Hours</p>
          </div>
        </div>
      </section>

      {/* ===== SECTION 6: HOW IT WORKS ===== */}
      <section className="py-16 px-6 bg-[#F9FAFB]">
        <div className="container mx-auto max-w-5xl">
          <h2 style={{ fontFamily: HEADING_FONT }} className="text-2xl md:text-4xl font-black text-center text-slate-900 mb-12">
            How It Works
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { n: '01', t: 'Learn Concepts',    d: 'Fundamentals → advanced',          c: '#4F46E5' },
              { n: '02', t: 'Build Campaigns',   d: 'Real budgets, real brands',        c: '#FF6B35' },
              { n: '03', t: 'Analyse & Optimise',d: 'GA4, dashboards, attribution',     c: '#2563EB' },
              { n: '04', t: 'Deliver Results',   d: 'Portfolio + case studies',         c: '#16A34A' },
            ].map((s) => (
              <div key={s.n} className="text-center">
                <div className="w-16 h-16 mx-auto rounded-2xl flex items-center justify-center text-white font-black text-xl shadow-lg"
                     style={{ backgroundColor: s.c, fontFamily: HEADING_FONT }}>
                  {s.n}
                </div>
                <h3 style={{ fontFamily: HEADING_FONT }} className="mt-4 text-base md:text-lg font-black text-slate-900">{s.t}</h3>
                <p className="text-sm text-slate-600">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== SALARY HIGHLIGHT BANNER ===== */}
      <section className="py-12 px-6">
        <div className="container mx-auto max-w-5xl">
          <div className="bg-[#FF6B35] rounded-3xl px-8 py-10 text-center text-white shadow-2xl">
            <p className="text-xs md:text-sm font-bold uppercase tracking-[0.3em] text-white/80 mb-2">Placement Salary Range</p>
            <p style={{ fontFamily: HEADING_FONT }} className="text-2xl md:text-4xl font-black leading-tight">
              Our students get placed at salaries between
              <span className="block text-3xl md:text-5xl mt-1">₹4 LPA to ₹20 LPA</span>
            </p>
          </div>
        </div>
      </section>

      {/* ===== SECTION 7: CAREER OUTCOMES ===== */}
      <section className="py-20 px-6 bg-[#F9FAFB]">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#4F46E5] mb-2">Career Outcomes</p>
            <h2 style={{ fontFamily: HEADING_FONT }} className="text-3xl md:text-5xl font-black text-slate-900 leading-tight">
              Career Opportunities After This Program
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {careers.map((c) => (
              <div key={c.t} className={`relative bg-white border ${c.popular ? 'border-[#FF6B35] shadow-xl' : 'border-slate-200'} rounded-2xl p-6 hover:shadow-lg transition-all`}>
                {c.popular && (
                  <span className="absolute -top-3 right-4 inline-block bg-[#FF6B35] text-white text-[10px] font-black uppercase tracking-widest rounded-full px-3 py-1 shadow-md">
                    HIGHEST DEMAND
                  </span>
                )}
                <div className="w-11 h-11 rounded-xl bg-[#4F46E5]/10 text-[#4F46E5] flex items-center justify-center mb-3">
                  <Briefcase className="w-5 h-5" />
                </div>
                <h3 style={{ fontFamily: HEADING_FONT }} className="text-lg font-black text-[#4F46E5] mb-2 leading-snug">{c.t}</h3>
                <p style={{ fontFamily: HEADING_FONT }} className="text-3xl font-black text-[#FF6B35] mb-1">{c.salary}</p>
                <p className="text-xs text-slate-500">{c.for_}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== SECTION 8: PERFECT FOR ===== */}
      <section className="py-20 px-6 bg-white">
        <div className="container mx-auto max-w-6xl">
          <h2 style={{ fontFamily: HEADING_FONT }} className="text-3xl md:text-5xl font-black text-center text-slate-900 mb-12">
            Perfect For
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {perfectFor.map((p) => (
              <div key={p.t} className="text-center bg-[#F5F3FF] rounded-2xl p-5 hover:bg-[#EEEAFF] transition-all">
                <div className="w-12 h-12 mx-auto rounded-2xl bg-white text-[#4F46E5] flex items-center justify-center shadow-sm mb-3">{p.icon}</div>
                <p style={{ fontFamily: HEADING_FONT }} className="text-sm font-bold text-slate-800">{p.t}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== SECTION 9: WHY AISCHOOL ===== */}
      <section className="py-20 px-6 bg-[#F9FAFB]">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#4F46E5] mb-2">The aiSchool Difference</p>
            <h2 style={{ fontFamily: HEADING_FONT }} className="text-3xl md:text-5xl font-black text-slate-900 leading-tight">
              Why Students Trust <span className="text-[#FF6B35]">aiSchool for All</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {why.map((w) => (
              <div key={w.t} className="bg-white border border-slate-200 rounded-2xl p-6 hover:shadow-lg transition-all">
                <div className="w-11 h-11 rounded-xl bg-[#16A34A]/10 text-[#16A34A] flex items-center justify-center mb-3">{w.icon}</div>
                <h3 style={{ fontFamily: HEADING_FONT }} className="text-lg font-black text-slate-900 mb-2">{w.t}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{w.d}</p>
              </div>
            ))}
          </div>

          {/* Founder quote */}
          <div className="mt-12 bg-white rounded-3xl p-8 md:p-10 shadow-xl border border-slate-100 max-w-3xl mx-auto">
            <p className="text-[#4F46E5] text-4xl leading-none mb-2">&ldquo;</p>
            <p style={{ fontFamily: HEADING_FONT }} className="text-lg md:text-xl text-slate-800 font-semibold leading-relaxed mb-4">
              Our mission is simple: bridge the gap between education and industry with practical, job-oriented learning.
            </p>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#4F46E5] to-[#FF6B35] flex items-center justify-center text-white font-black">MK</div>
              <div>
                <p style={{ fontFamily: HEADING_FONT }} className="font-black text-slate-900">Madhur Khera</p>
                <p className="text-xs text-slate-500">Founder &amp; CEO, aiSchool for All</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== OUTCOME GUARANTEE BOX ===== */}
      <section className="py-12 px-6 bg-white">
        <div className="container mx-auto max-w-4xl">
          <div className="bg-white border-2 border-[#FF6B35] rounded-3xl p-8 md:p-10 shadow-xl text-center">
            <ShieldCheck className="w-12 h-12 text-[#FF6B35] mx-auto mb-4" />
            <h3 style={{ fontFamily: HEADING_FONT }} className="text-2xl md:text-3xl font-black text-slate-900 mb-3">
              Get Hired in 4 Months — Or We Help You Until You Do.
            </h3>
            <p className="text-slate-600 max-w-xl mx-auto">That&apos;s our promise. Career support, resume prep, mock interviews and direct intros to 200+ hiring partners — until you land the role.</p>
            <p style={{ fontFamily: HEADING_FONT }} className="mt-4 italic text-[#4F46E5] font-bold">— Madhur Khera, Founder</p>
          </div>
        </div>
      </section>

      {/* ===== SECTION 10: PROGRAM DETAILS + LEAD FORM ===== */}
      <section id="enroll" className="py-20 px-6 bg-white">
        <div className="container mx-auto max-w-6xl">
          <div className="grid lg:grid-cols-2 rounded-3xl overflow-hidden shadow-2xl">

            {/* LEFT — Program Summary (Purple) */}
            <div className="bg-[#4F46E5] text-white p-8 md:p-12">
              <p className="text-xs font-bold uppercase tracking-[0.25em] text-white/70 mb-2">Program Details</p>
              <h2 style={{ fontFamily: HEADING_FONT }} className="text-3xl md:text-4xl font-black mb-6 leading-tight">
                Everything You&apos;ll Get
              </h2>

              <ul className="space-y-3 text-base mb-8">
                {[
                  '200 Hours Live Training',
                  'Real Campaign Projects',
                  '₹4–20 LPA Salary Range',
                  'AI Tools Included',
                  'Industry-Aligned Certificate',
                  'Lifetime Career Support',
                ].map((b) => (
                  <li key={b} className="flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 text-[#FF6B35] flex-shrink-0" />
                    <span className="font-semibold">{b}</span>
                  </li>
                ))}
              </ul>

              <div className="grid grid-cols-2 gap-4 mb-8 text-sm">
                {[
                  ['Duration', '5 Months'],
                  ['Learning Hours', '200 Hours'],
                  ['Mode', 'Live Online'],
                  ['Batch Size', 'Small Batches'],
                ].map(([k, v]) => (
                  <div key={k} className="bg-white/10 rounded-xl p-3">
                    <p className="text-[10px] uppercase tracking-widest text-white/70">{k}</p>
                    <p style={{ fontFamily: HEADING_FONT }} className="font-black text-white text-base">{v}</p>
                  </div>
                ))}
              </div>

              <div className="bg-white/10 rounded-2xl p-4 mb-6">
                <p className="text-[10px] uppercase tracking-widest text-white/70 mb-1">Limited Time Offer</p>
                <div className="flex items-baseline gap-3 flex-wrap">
                  <span className="text-xl text-white/50 line-through font-semibold">₹95,000</span>
                  <span style={{ fontFamily: HEADING_FONT }} className="text-4xl font-black text-[#FF6B35]">₹35,000</span>
                </div>
                <p className="text-xs text-white/80 mt-1">You save ₹60,000 · EMI available</p>
              </div>

              <a href={`tel:${BRAND.PHONE_TEL}`} className="inline-flex items-center gap-2 text-white font-bold text-base">
                <Phone className="w-4 h-4" /> {BRAND.PHONE_DISPLAY}
              </a>
            </div>

            {/* RIGHT — Lead Form */}
            <div className="bg-white p-8 md:p-12">
              {submitted ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 mx-auto rounded-full bg-[#16A34A]/10 text-[#16A34A] flex items-center justify-center mb-4">
                    <CheckCircle2 className="w-8 h-8" />
                  </div>
                  <h3 style={{ fontFamily: HEADING_FONT }} className="text-2xl font-black text-slate-900 mb-2">You&apos;re In!</h3>
                  <p className="text-slate-600">Our counselor will WhatsApp you within 1 hour at <strong>{form.phone}</strong>.</p>
                  <a href={BRAND.WA_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 mt-6 bg-[#16A34A] text-white font-bold px-6 py-3 rounded-xl">
                    <MessageCircle className="w-5 h-5" /> Open WhatsApp Now
                  </a>
                </div>
              ) : (
                <>
                  <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#FF6B35] mb-2">Start Your Journey Today</p>
                  <h2 style={{ fontFamily: HEADING_FONT }} className="text-3xl md:text-4xl font-black text-[#4F46E5] mb-6 leading-tight">
                    Book Your FREE Demo Class
                  </h2>

                  <form onSubmit={submitDemo} className="space-y-4" data-testid="pm-lead-form">
                    <div>
                      <input
                        required type="text" placeholder="Full Name *"
                        value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                        data-testid="pm-form-name"
                        className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3.5 text-slate-900 focus:outline-none focus:border-[#4F46E5] focus:bg-white"
                      />
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <input
                        required type="tel" placeholder="Phone *"
                        value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                        data-testid="pm-form-phone"
                        className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3.5 text-slate-900 focus:outline-none focus:border-[#4F46E5] focus:bg-white"
                      />
                      <input
                        required type="email" placeholder="Email *"
                        value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                        data-testid="pm-form-email"
                        className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3.5 text-slate-900 focus:outline-none focus:border-[#4F46E5] focus:bg-white"
                      />
                    </div>
                    <select
                      value={form.course} onChange={(e) => setForm({ ...form, course: e.target.value })}
                      data-testid="pm-form-course"
                      className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3.5 text-slate-900 focus:outline-none focus:border-[#4F46E5] focus:bg-white"
                    >
                      {['Performance Marketing Course', 'AI Bootcamp', 'Film Making & Animation', 'Game Development', 'Jewellery Designing', 'Blender & Character Design'].map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                    <input
                      type="text" placeholder="City"
                      value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })}
                      data-testid="pm-form-city"
                      className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3.5 text-slate-900 focus:outline-none focus:border-[#4F46E5] focus:bg-white"
                    />

                    {formErr && <p className="text-red-600 text-sm">{formErr}</p>}

                    <button
                      type="submit" disabled={submitting}
                      data-testid="pm-form-submit"
                      style={{ fontFamily: HEADING_FONT }}
                      className="w-full inline-flex items-center justify-center gap-2 bg-[#FF6B35] hover:bg-[#E55A24] disabled:opacity-60 text-white font-black py-4 rounded-xl text-base shadow-lg shadow-[#FF6B35]/30 transition-all"
                    >
                      {submitting ? 'Submitting...' : <>Book My FREE Demo <ArrowRight className="w-5 h-5" /></>}
                    </button>

                    <div className="grid sm:grid-cols-3 gap-3 text-xs text-slate-500 text-center pt-2">
                      <span className="inline-flex items-center justify-center gap-1.5">🔒 No Spam</span>
                      <span className="inline-flex items-center justify-center gap-1.5">📞 Call within 1 hour</span>
                      <span className="inline-flex items-center justify-center gap-1.5">✓ Free Demo Class</span>
                    </div>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ===== SECTION 11: FAQ ===== */}
      <section id="faq" className="py-20 px-6 bg-[#F9FAFB]">
        <div className="container mx-auto max-w-3xl">
          <div className="text-center mb-12">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#4F46E5] mb-2">Frequently Asked</p>
            <h2 style={{ fontFamily: HEADING_FONT }} className="text-3xl md:text-5xl font-black text-slate-900 leading-tight">
              Got Questions? <span className="text-[#FF6B35]">We Have Answers</span>
            </h2>
          </div>
          <div className="space-y-3">
            {faqs.map((f, i) => (
              <div key={i} className={`bg-white border rounded-2xl overflow-hidden transition-all ${openFAQ === i ? 'border-[#4F46E5] shadow-md' : 'border-slate-200'}`}>
                <button
                  onClick={() => setOpenFAQ(openFAQ === i ? -1 : i)}
                  className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left"
                  data-testid={`pm-faq-${i}`}
                >
                  <span style={{ fontFamily: HEADING_FONT }} className="font-bold text-[#4F46E5]">{f.q}</span>
                  <ChevronDown className={`w-5 h-5 text-[#FF6B35] flex-shrink-0 transition-transform ${openFAQ === i ? 'rotate-180' : ''}`} />
                </button>
                {openFAQ === i && (<div className="px-6 pb-5 text-slate-600 leading-relaxed">{f.a}</div>)}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== SECTION 12: FINAL CTA BANNER ===== */}
      <section className="py-20 px-6 bg-gradient-to-r from-[#4F46E5] to-[#2563EB] text-white">
        <div className="container mx-auto max-w-5xl text-center">
          <h2 style={{ fontFamily: HEADING_FONT }} className="text-3xl md:text-5xl font-black mb-3 leading-tight">
            Your Career in Digital Marketing <span className="text-[#FF6B35]">Starts Here</span>
          </h2>
          <p className="text-white/80 text-lg mb-8">200 Hours · 5 Months · Live Online · AI Tools Included</p>

          <div className="flex flex-col sm:flex-row gap-3 justify-center max-w-2xl mx-auto">
            <Link
              to="/book-demo"
              data-testid="pm-final-enroll"
              style={{ fontFamily: HEADING_FONT }}
              className="flex-1 inline-flex items-center justify-center gap-2 bg-white hover:bg-slate-100 text-[#4F46E5] px-8 py-4 rounded-xl font-black text-lg shadow-2xl transition-all hover:scale-105"
            >
              Enroll Now <ArrowRight className="w-5 h-5" />
            </Link>
            <a
              href={BRAND.WA_URL}
              target="_blank" rel="noopener noreferrer"
              data-testid="pm-final-whatsapp"
              style={{ fontFamily: HEADING_FONT }}
              className="flex-1 inline-flex items-center justify-center gap-2 bg-transparent hover:bg-white/10 text-white border-2 border-[#FF6B35] px-8 py-4 rounded-xl font-black text-lg transition-all"
            >
              <MessageCircle className="w-5 h-5" /> WhatsApp {BRAND.PHONE_DISPLAY}
            </a>
          </div>
        </div>
      </section>

      <SiteFooter accent="blue" />

      {/* Desktop floating WhatsApp button */}
      <a
        href={BRAND.WA_URL}
        target="_blank" rel="noopener noreferrer"
        data-testid="pm-floating-whatsapp"
        className="hidden md:flex fixed bottom-6 right-6 z-[55] items-center gap-2 bg-[#16A34A] hover:bg-[#15803D] text-white px-5 py-3 rounded-full shadow-2xl shadow-green-900/30 font-bold transition-all hover:scale-105"
      >
        <MessageCircle className="w-5 h-5" /> Chat Now
      </a>

      <MobileCTABar courseName="Performance Marketing" />
    </div>
  );
};

export default PerformanceMarketingPage;
